import { AssignTable } from '@shared/components/assign-table';
import { CompanyPolicyResponse } from '@interfaces/models';
import { ComboboxItem, Typography } from '@ui/components';
import { formatPhoneNumber } from '@helpers/utils';
import { useHasPermission } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';

import { useEmployeesProfileAssignEmployees } from './use-employees-profile-assign-employees';

interface EmployeesProfileAssignEmployeesProps {
  data?: CompanyPolicyResponse;
}

export const EmployeesProfileAssignEmployees: FC<EmployeesProfileAssignEmployeesProps> = ({
  data,
}) => {
  const { assignTable, setAssignedUsers, assignedUsers, totalItems } =
    useEmployeesProfileAssignEmployees(data);
  const { isLoading, combobox, getRowId, table } = assignTable;
  const { t } = useTranslation();
  const hasAssignPermission = useHasPermission(
    'EMPLOYEES_PROFILE_ASSIGN_EMPLOYEES_TO_PROFILE',
    'WRITE',
  );

  return (
    <AssignTable
      text={{
        title: `${t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.TABLE_TITLE')} (${totalItems})`,
        comboboxPlaceholder: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.COMBOBOX_PLACEHOLDER'),
        comboboxNoResults: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.COMBOBOX_NO_RESULTS'),
        searchPlaceholder: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.SEARCH_PLACEHOLDER'),
        tableNoResults: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.TABLE_NO_RESULTS'),
        removeButtonText: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.REMOVE'),
        assignButtonText: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.ADD'),
        selectedRows: (count: number) =>
          t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.SELECTED_ROWS', {
            postProcess: 'interval',
            count,
          }),
      }}
      itemTemplate={(item) => (
        <ComboboxItem
          keywords={[item.firstName ?? '', item.lastName ?? '', item.phoneNumber]}
          disabled={data?.assignedEmployees?.includes(item.id)}
          className="justify-between h-12"
          label={item.id}
          value={item.id}
          key={item.id}
        >
          <Typography variant="labelMediumBook">
            {item.firstName ?? '-'} {item.lastName ?? '-'}
          </Typography>
          <Typography variant="labelMediumBook">
            {formatPhoneNumber(item.phoneNumber, item.phoneExt)}
          </Typography>
        </ComboboxItem>
      )}
      onSelectedChange={(value) => setAssignedUsers(value)}
      disabled={data?.isDefault || !hasAssignPermission}
      className="h-min shadow-xs"
      selected={assignedUsers}
      isLoading={isLoading}
      combobox={combobox}
      getRowId={getRowId}
      table={table}
    />
  );
};
